// =============================================================================
// The shared dimming overlay.
//
// The mockup computes `overlayOpen: drawerOpen || (shopMenuOpen && !isNarrow)`,
// so one element is driven by two unrelated features. Rather than let each of
// them set and clear `.is-open` — where whichever closed last would wrongly
// hide the overlay the other still needs — callers claim and release it by
// name, and the class follows whether any claim is outstanding.
// =============================================================================

const claims = new Set();
const listeners = new Set();

function element() {
	return document.querySelector('[data-ch-overlay]');
}

function sync() {
	const el = element();

	if (el) {
		el.classList.toggle('is-open', claims.size > 0);
	}

	// A claim means a panel is covering the page, so the page behind it should
	// not scroll. Doing it here rather than in the cart and the nav separately is
	// the same reasoning as the class itself: whichever closed last would
	// otherwise free the scroll the other still needs held.
	document.body.classList.toggle('ch-no-scroll', claims.size > 0);
}

/**
 * Show the overlay on behalf of `owner`.
 *
 * @param {string} owner Stable name for the feature holding it open.
 */
export function claimOverlay(owner) {
	claims.add(owner);
	sync();
}

/**
 * Drop `owner`'s claim. The overlay hides once no claims remain.
 *
 * @param {string} owner The same name passed to claimOverlay().
 */
export function releaseOverlay(owner) {
	claims.delete(owner);
	sync();
}

/**
 * Run `fn` when the overlay is clicked, or Escape is pressed.
 *
 * Both gestures mean "dismiss whatever is open" in the mockup, so they share
 * one entry point and every feature closes together — matching closeOverlays().
 *
 * @param {Function} fn Callback.
 */
export function onDismiss(fn) {
	listeners.add(fn);
}

function dismissAll() {
	listeners.forEach((fn) => fn());
}

document.addEventListener('click', (event) => {
	if (event.target instanceof Element && event.target.closest('[data-ch-overlay]')) {
		dismissAll();
	}
});

document.addEventListener('keydown', (event) => {
	if (event.key === 'Escape' && claims.size > 0) {
		dismissAll();
	}
});
